import { useIsDemo } from "@/demo/DemoContext";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OrganisationSettingsDialog } from "@/components/OrganisationSettingsDialog";
import { CreateSupplierDialog } from "@/components/CreateSupplierDialog";
import { SpendSummaryCard } from "@/components/SpendSummaryCard";
import { useApOnboardingStatus } from "@/hooks/useApOnboardingStatus";
import { useApBills } from "@/hooks/useApBills";
import { useApStats } from "@/hooks/useApStats";
import { useChat } from "@/components/chat/ChatProvider";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import type { components } from "@dolfin/api-admin";
import { CheckIcon, ChevronRight, Upload } from "lucide-react";
import { cn } from "@/lib/utils";
import { PageContainer, PageHeader, StatCard } from "@/components/PagePrimitives";
import { Eyebrow, LoadingState } from "@/components/PageUi";
import { RowPanel, type PanelRow } from "@/components/RowPanel";

const steps = [
  {
    key: "accountSetUp" as const,
    title: "Set up your account",
    description: "Add your business details, bank account, and preferences before getting started.",
  },
  {
    key: "supplierCreated" as const,
    title: "Add a supplier",
    description: "Every bill needs a supplier. Create one to get started.",
  },
  {
    key: "billCreated" as const,
    title: "Create a bill",
    description: "Enter what you owe, to whom, and when it's due.",
  },
  {
    key: "billApproved" as const,
    title: "Approve and pay",
    description: "Review the bill and trigger a test payment.",
  },
];

export function ApOnboardingPage() {
  const isDemo = useIsDemo();
  const navigate = useNavigate();
  const { activeOrganisationId } = useAuth();
  const status = useApOnboardingStatus();
  const patchedRef = useRef(false);

  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);

  useEffect(() => {
    if (
      status.completedCount === 4 &&
      !status.hasBeenOnboarded &&
      !patchedRef.current &&
      activeOrganisationId &&
      !isDemo
    ) {
      patchedRef.current = true;
      api.PATCH("/v1/organisations/{id}", {
        params: { path: { id: activeOrganisationId } },
        body: { hasBeenOnboarded: true },
      }).then(() => status.refresh());
    }
  }, [status.completedCount, status.hasBeenOnboarded, activeOrganisationId, isDemo]);

  function handleStepClick(index: number) {
    switch (index) {
      case 0:
        setOrgDialogOpen(true);
        break;
      case 1:
        setSupplierDialogOpen(true);
        break;
      case 2:
        navigate("/bills");
        break;
      case 3:
        navigate("/bills");
        break;
    }
  }

  // A demo arrives fully seeded: it never sees the checklist.
  if (isDemo) {
    return <ApOverview onUploadBill={() => navigate("/bills")} />;
  }

  if (status.loading) {
    return (
      <PageContainer>
        <LoadingState label="Loading accounts payable..." />
      </PageContainer>
    );
  }

  if (status.hasBeenOnboarded) {
    return <ApOverview onUploadBill={() => navigate("/bills")} />;
  }

  return (
    <OnboardingSteps
      badge="AP sandbox"
      title="Accounts payable"
      intro="No real data, no real payments. Follow the steps or ask the assistant."
      steps={steps.map((step) => ({ ...step, complete: status.steps[step.key] }))}
      onStep={handleStepClick}
    >
      <OrganisationSettingsDialog
        open={orgDialogOpen}
        onOpenChange={setOrgDialogOpen}
        onSaved={() => status.refresh()}
        mode="ap-onboarding"
      />
      <CreateSupplierDialog
        open={supplierDialogOpen}
        onOpenChange={setSupplierDialogOpen}
        onCreated={() => status.refresh()}
      />
    </OnboardingSteps>
  );
}

export function OnboardingSteps({
  badge,
  title,
  intro,
  steps: items,
  onStep,
  children,
}: {
  badge: string;
  title: string;
  intro: React.ReactNode;
  steps: { key: string; title: string; description: string; complete: boolean }[];
  onStep: (index: number) => void;
  children?: React.ReactNode;
}) {
  const done = items.filter((s) => s.complete).length;
  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center p-4 sm:p-8">
      <Badge variant="outline" className="mb-4 gap-2 rounded-full border-accent-blue/25 bg-accent-blue/10 text-accent-blue">
        <span className="size-1.5 rounded-full bg-accent-blue" />
        {badge}
      </Badge>
      <h1 className="text-2xl font-bold tracking-tight @sm:text-3xl">{title}</h1>
      <p className="mt-2 mb-8 text-center text-sm text-pretty text-muted-foreground">{intro}</p>

      <section className="w-full rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-3">
          <Eyebrow>Start here</Eyebrow>
          <span className="text-xs text-muted-foreground tabular-nums">
            {done} of {items.length} complete
          </span>
        </div>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-accent-blue transition-[width]"
            style={{ width: `${(done / Math.max(items.length, 1)) * 100}%` }}
          />
        </div>
        <ol className="mt-3 divide-y">
          {items.map((step, i) => (
            <li key={step.key}>
              <button
                type="button"
                className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-4 rounded-lg px-2 py-4 text-left transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                onClick={() => onStep(i)}
              >
                <span
                  className={cn(
                    "grid size-8 shrink-0 place-items-center rounded-lg border text-sm font-semibold tabular-nums",
                    step.complete
                      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600"
                      : "bg-muted/50 text-muted-foreground",
                  )}
                >
                  {step.complete ? <CheckIcon className="size-4" /> : i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className={cn("font-semibold", step.complete && "text-muted-foreground line-through decoration-muted-foreground/40")}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </button>
            </li>
          ))}
        </ol>
      </section>
      {children}
    </div>
  );
}

type BillResponse = components["schemas"]["BillResponse"];
type CurrencyTotal = components["schemas"]["CurrencyTotal"];

const num = (v: number | string | null | undefined): number =>
  typeof v === "number" ? v : v == null ? 0 : Number(v) || 0;

function primaryCurrency(list?: CurrencyTotal[]): CurrencyTotal | null {
  return list && list.length > 0 ? list[0]! : null;
}

function money(currency: string | undefined, amount: number | string | null | undefined): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "GBP",
      maximumFractionDigits: 0,
    }).format(num(amount));
  } catch {
    return `${Math.round(num(amount))}`;
  }
}

function scheduledDate(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return format(new Date(iso), "EEE d MMM");
  } catch {
    return "";
  }
}

function billRows(bills: BillResponse[], scheduled: boolean, open: (id: string) => void): PanelRow[] {
  return bills.map((bill, i) => {
    const supplier = bill.supplierName ?? bill.supplier?.name ?? "Supplier";
    const scheduledFor = scheduled ? scheduledDate(bill.paymentDate) : "";
    return {
      key: bill.id ?? `bill-${i}`,
      primary: supplier,
      secondary: [bill.invoiceNumber ?? "Bill", scheduledFor].filter(Boolean).join(" · "),
      right: money(bill.currency, bill.totalAmount),
      onOpen: bill.id ? () => open(bill.id!) : undefined,
    };
  });
}

function ApOverview({ onUploadBill }: { onUploadBill: () => void }) {
  const navigate = useNavigate();
  const awaiting = useApBills("NeedsApproval", 5);
  const scheduledPayments = useApBills("Scheduled", 5);
  const stats = useApStats();
  const { openChat, sendMessage } = useChat();

  const askAgent = (prompt: string) => {
    openChat();
    void sendMessage(prompt);
  };

  const dash = stats.loading ? "..." : "-";

  const toPayPrimary = primaryCurrency(stats.outstanding?.totals.toPayByCurrency);
  const toPaySub = stats.outstanding
    ? `${stats.outstanding.totals.billCount} bill${stats.outstanding.totals.billCount === 1 ? "" : "s"}`
    : undefined;

  const overduePrimary = primaryCurrency(stats.overdue?.totals.overdueByCurrency);
  const overdueSub = stats.overdue
    ? `${stats.overdue.totals.supplierCount} supplier${stats.overdue.totals.supplierCount === 1 ? "" : "s"}`
    : undefined;

  const paid = stats.paid30;
  const deltaPct = paid?.trend.deltaPercent != null ? num(paid.trend.deltaPercent) : null;
  const paidDelta = deltaPct === null
    ? paid
      ? "vs £0 prior"
      : undefined
    : `${deltaPct >= 0 ? "+" : ""}${Math.round(deltaPct * 100)}% vs prior 30d`;
  const paidTrend =
    paid?.trend.direction === "up" ? "up" : paid?.trend.direction === "down" ? "down" : undefined;

  // Days-to-pay rides on the Paid card: the trend keeps its red/green colour,
  // the timing segment stays neutral.
  const timing = paid?.paymentTiming;
  const timingSub =
    timing?.averageDaysToPay != null
      ? `${Math.round(num(timing.averageDaysToPay))}d avg to pay${
          timing.verdict === "on_terms" ? " · on terms" : timing.verdict === "late" ? " · late" : ""
        }`
      : null;
  const paidSub = paidDelta ? (
    <>
      {paidDelta}
      {timingSub && <span className="text-muted-foreground"> · {timingSub}</span>}
    </>
  ) : (
    timingSub ?? undefined
  );

  // The headline is the primary currency only; other currencies are in a tooltip.
  const paidCurrencies = paid?.paidByCurrency ?? [];
  const hasOtherCurrencies = paidCurrencies.some((c) => c.currency !== paid?.currency);
  const paidValue = !paid ? (
    dash
  ) : !hasOtherCurrencies ? (
    money(paid.currency, paid.summary.totalPaid)
  ) : (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help underline decoration-dotted decoration-muted-foreground/50 underline-offset-4">
          {money(paid.currency, paid.summary.totalPaid)}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-0.5">
          {paidCurrencies.map((c) => (
            <div key={c.currency} className="flex items-center justify-between gap-4 tabular-nums">
              <span>{money(c.currency, c.amount)}</span>
              <span className="opacity-70">
                {num(c.count)} bill{num(c.count) === 1 ? "" : "s"}
              </span>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );

  const topSuppliers = stats.paid90?.topSuppliers ?? [];
  const topSupplierRows: PanelRow[] = topSuppliers.slice(0, 5).map((s, i) => ({
    key: s.supplierId ?? `top-${i}`,
    primary: s.supplierName,
    secondary: `${num(s.billCount)} bill${num(s.billCount) === 1 ? "" : "s"} paid`,
    right: money(stats.paid90?.currency, s.paid),
    onOpen: s.supplierId ? () => navigate(`/suppliers/${s.supplierId}`) : undefined,
  }));
  const openBill = (id: string) => navigate(`/bills/${id}`);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Payables"
        title="Overview"
        actions={
          <Button onClick={onUploadBill} className="gap-1.5">
            <Upload className="size-4" />
            Upload bill
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 @2xl:grid-cols-3">
        <StatCard
          label="To pay"
          value={toPayPrimary ? money(toPayPrimary.currency, toPayPrimary.amount) : stats.outstanding ? money(undefined, 0) : dash}
          sub={toPaySub}
          onAskAgent={() => askAgent("What do I have to pay, and is anything overdue or due soon?")}
        />
        <StatCard
          label="Overdue"
          value={overduePrimary ? money(overduePrimary.currency, overduePrimary.amount) : stats.overdue ? money(toPayPrimary?.currency, 0) : dash}
          sub={overdueSub}
          trend={overduePrimary ? "down" : undefined}
          onAskAgent={() => askAgent("Show me my overdue bills and what to do about each one.")}
        />
        <StatCard
          label="Paid · 30d"
          value={paidValue}
          sub={paidSub}
          trend={paidTrend}
          onAskAgent={() => askAgent("Summarise what I've paid over the last 30 days and how fast I'm paying - am I on terms?")}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-3 @xl:grid-cols-2">
        <RowPanel
          title="Awaiting approval"
          rows={billRows(awaiting.bills, false, openBill)}
          loading={awaiting.loading}
          empty="Nothing awaiting approval."
        />
        <RowPanel
          title="Scheduled payments"
          rows={billRows(scheduledPayments.bills, true, openBill)}
          loading={scheduledPayments.loading}
          empty="No scheduled payments."
        />
      </div>

      <div className="mt-5">
        <RowPanel
          title="Top suppliers · last 90 days"
          rows={topSupplierRows}
          loading={stats.loading}
          empty="No bills paid in the last 90 days."
        />
      </div>

      <div className="mt-5">
        <SpendSummaryCard onBillClick={(id) => navigate(`/bills/${id}`)} />
      </div>
    </PageContainer>
  );
}
